

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, X } from 'lucide-react';
import { getAssistantIdForGender } from '@/lib/vapi-config';
import { supabase } from '@/lib/supabase-client';

interface Question {
  question: string;
  expected_answer: string;
  keywords: string;
}

export default function EditJobPage() {
  const navigate = useNavigate();
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unauthorized, setUnauthorized] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    company_name: '',
    description: '',
    requirements: '',
    tech_stack: '',
    location: '',
    salary_range: '',
    employment_type: 'full-time',
    posted_by: ''
  });
  const [interviewConfig, setInterviewConfig] = useState({
    id: '',
    name: '',
    personality: '',
    interview_rules: '',
    interview_type: 'behavioral',
    difficulty_level: 'mid',
    max_duration_minutes: 10,
    max_questions: 5,
    voice_gender: 'male' as 'male' | 'female',
    randomize_questions: false
  });
  const [questions, setQuestions] = useState<Question[]>([
    { question: '', expected_answer: '', keywords: '' }
  ]);

  useEffect(() => {
    if (params.id) {
      fetchJobData();
    }
  }, [params.id]);

  const fetchJobData = async () => {
    try {
      // Check if user is authenticated and owns this job
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      // Fetch job with interview configuration
      const { data: job, error } = await supabase
        .from('job_postings')
        .select(`
          *,
          interview_configurations (
            id,
            name,
            personality,
            interview_rules,
            interview_type,
            difficulty_level,
            max_duration_minutes,
            max_questions,
            default_questions,
            voice_gender,
            randomize_questions
          )
        `)
        .eq('id', params.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching job:', error);
        throw new Error(error.message);
      }

      if (!job) {
        throw new Error('Job not found');
      }

      // Check ownership
      if (job.user_id !== user.id) {
        setUnauthorized(true);
        setLoading(false);
        return;
      }

      if (job) {
        setFormData({
          title: job.title || '',
          company_name: job.company_name || '',
          description: job.description || '',
          requirements: job.requirements || '',
          tech_stack: job.tech_stack || '',
          location: job.location || '',
          salary_range: job.salary_range || '',
          employment_type: job.employment_type || 'full-time',
          posted_by: job.posted_by || ''
        });

        if (job.interview_configurations) {
          const config = job.interview_configurations;
          setInterviewConfig({
            id: config.id || '',
            name: config.name || '',
            personality: config.personality || '',
            interview_rules: config.interview_rules || '',
            interview_type: config.interview_type || 'behavioral',
            difficulty_level: config.difficulty_level || 'mid',
            max_duration_minutes: config.max_duration_minutes || 10,
            max_questions: config.max_questions || 5,
            voice_gender: (config.voice_gender || 'male') as 'male' | 'female',
            randomize_questions: config.randomize_questions || false
          });

          const configQuestions = config.default_questions?.questions || [];
          if (configQuestions.length > 0) {
            setQuestions(
              configQuestions.map((q: any) => {
                if (typeof q === 'string') {
                  return {
                    question: q,
                    expected_answer: '',
                    keywords: ''
                  };
                }
                return {
                  question: q.question || '',
                  expected_answer: q.expected_answer || '',
                  keywords: Array.isArray(q.keywords) ? q.keywords.join(', ') : (q.keywords || '')
                };
              })
            );
          }
        }
      }
    } catch (error) {
      console.error('Error fetching job:', error);
      alert('Error loading job data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const questionsJson = questions
        .filter(q => q.question.trim())
        .map(q => ({
          question: q.question,
          expected_answer: q.expected_answer,
          keywords: q.keywords.split(',').map(k => k.trim()).filter(k => k)
        }));

      const configName = interviewConfig.name || `${formData.title} Interview`;
      const roleStatement = `You are an AI interviewer conducting an interview for a ${formData.title} position at ${formData.company_name}. ${formData.description}`;
      const assistantId = getAssistantIdForGender(interviewConfig.voice_gender);

      // Update interview configuration
      const { error: configError } = await supabase
        .from('interview_configurations')
        .update({
          name: configName,
          assistant_id: assistantId,
          role_statement: roleStatement,
          personality: interviewConfig.personality,
          interview_rules: interviewConfig.interview_rules,
          default_questions: { questions: questionsJson },
          difficulty_level: interviewConfig.difficulty_level,
          interview_type: interviewConfig.interview_type,
          max_duration_minutes: interviewConfig.max_duration_minutes,
          max_questions: interviewConfig.max_questions,
          voice_gender: interviewConfig.voice_gender,
          randomize_questions: interviewConfig.randomize_questions
        })
        .eq('id', interviewConfig.id);

      if (configError) {
        console.error('Error updating config:', configError);
        alert(`Failed to update interview configuration: ${configError.message}`);
        setSaving(false);
        return;
      }

      // Update job posting
      const { error: jobError } = await supabase
        .from('job_postings')
        .update({
          ...formData,
          updated_at: new Date().toISOString()
        })
        .eq('id', params.id);

      if (jobError) {
        console.error('Error updating job:', jobError);
        alert(`Failed to update job posting: ${jobError.message}`);
      } else {
        navigate(`/jobs/${params.id}`);
      }
    } catch (error) {
      console.error('Error updating job:', error);
      alert('Error updating job posting');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleConfigChange = (field: string, value: string | number | boolean) => {
    setInterviewConfig((prev) => ({ ...prev, [field]: value }));
  };

  const addQuestion = () => {
    setQuestions([...questions, { question: '', expected_answer: '', keywords: '' }]);
  };

  const removeQuestion = (index: number) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((_, i) => i !== index));
    }
  };

  const updateQuestion = (index: number, field: keyof Question, value: string) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 text-center">
            <div className="text-red-600 mb-4">
              <X className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Unauthorized Access</h2>
            <p className="text-gray-600 mb-6">
              You don't have permission to edit this job posting. Only the creator can edit their own jobs.
            </p>
            <Button onClick={() => navigate('/jobs')} className="bg-blue-600 hover:bg-blue-700">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Jobs
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(`/jobs/${params.id}`)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Job
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Edit Job Posting</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="e.g. Senior Frontend Developer"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    required
                    value={formData.company_name}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="e.g. TechCorp Inc."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Job Description *</Label>
                <Textarea
                  id="description"
                  required
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe the role, responsibilities, and what the candidate will be doing..."
                  rows={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="requirements">Requirements *</Label>
                <Textarea
                  id="requirements"
                  required
                  value={formData.requirements}
                  onChange={(e) => handleChange('requirements', e.target.value)}
                  placeholder="List the qualifications, experience, and skills required..."
                  rows={5}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tech_stack">Tech Stack</Label>
                <Input
                  id="tech_stack"
                  value={formData.tech_stack}
                  onChange={(e) => handleChange('tech_stack', e.target.value)}
                  placeholder="e.g. React, TypeScript, Node.js, PostgreSQL"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="e.g. San Francisco, CA / Remote"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="salary_range">Salary Range</Label>
                  <Input
                    id="salary_range"
                    value={formData.salary_range}
                    onChange={(e) => handleChange('salary_range', e.target.value)}
                    placeholder="e.g. $120k - $180k"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="employment_type">Employment Type</Label>
                <Select
                  value={formData.employment_type}
                  onValueChange={(value) => handleChange('employment_type', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full-time">Full-time</SelectItem>
                    <SelectItem value="part-time">Part-time</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-xl font-semibold mb-4">Interview Configuration</h3>

                <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                  <div className="space-y-2">
                    <Label>Configuration Name (Optional)</Label>
                    <Input
                      value={interviewConfig.name}
                      onChange={(e) => handleConfigChange('name', e.target.value)}
                      placeholder="Leave blank to auto-generate"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Interviewer Personality *</Label>
                    <Textarea
                      required
                      value={interviewConfig.personality}
                      onChange={(e) => handleConfigChange('personality', e.target.value)}
                      placeholder="e.g. Warm and friendly, Professional and encouraging..."
                      rows={2}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Interview Rules *</Label>
                    <Textarea
                      required
                      value={interviewConfig.interview_rules}
                      onChange={(e) => handleConfigChange('interview_rules', e.target.value)}
                      placeholder="e.g. Ask questions one at a time. Listen carefully. Focus on practical experience..."
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Interview Type</Label>
                      <Select
                        value={interviewConfig.interview_type}
                        onValueChange={(value) => handleConfigChange('interview_type', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="technical">Technical</SelectItem>
                          <SelectItem value="behavioral">Behavioral</SelectItem>
                          <SelectItem value="mixed">Mixed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Difficulty Level</Label>
                      <Select
                        value={interviewConfig.difficulty_level}
                        onValueChange={(value) => handleConfigChange('difficulty_level', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="entry">Entry</SelectItem>
                          <SelectItem value="mid">Mid</SelectItem>
                          <SelectItem value="senior">Senior</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Interviewer Voice</Label>
                      <Select
                        value={interviewConfig.voice_gender}
                        onValueChange={(value) => handleConfigChange('voice_gender', value)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Duration (minutes)</Label>
                      <Input
                        type="number"
                        min="2"
                        max="60"
                        value={interviewConfig.max_duration_minutes}
                        onChange={(e) => handleConfigChange('max_duration_minutes', parseInt(e.target.value))}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Max Questions</Label>
                      <Input
                        type="number"
                        min="3"
                        max="20"
                        value={interviewConfig.max_questions}
                        onChange={(e) => handleConfigChange('max_questions', parseInt(e.target.value))}
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="randomize_questions"
                      checked={interviewConfig.randomize_questions}
                      onCheckedChange={(checked) => handleConfigChange('randomize_questions', checked as boolean)}
                    />
                    <Label htmlFor="randomize_questions" className="cursor-pointer">
                      Randomize question order for each interview
                    </Label>
                  </div>

                  <div className="space-y-4 mt-6">
                    <div className="flex items-center justify-between">
                      <Label className="text-lg">Interview Questions</Label>
                      <Button
                        type="button"
                        onClick={addQuestion}
                        size="sm"
                        variant="outline"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Question
                      </Button>
                    </div>

                    {questions.map((q, index) => (
                      <Card key={index} className="bg-white">
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 space-y-2">
                                <Label>Question {index + 1}</Label>
                                <Textarea
                                  value={q.question}
                                  onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                                  placeholder="Enter the interview question..."
                                  rows={2}
                                />
                              </div>
                              {questions.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeQuestion(index)}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              )}
                            </div>

                            <div className="space-y-2">
                              <Label>Expected Answer / Context</Label>
                              <Textarea
                                value={q.expected_answer}
                                onChange={(e) => updateQuestion(index, 'expected_answer', e.target.value)}
                                placeholder="What kind of answer are you looking for? (Optional)"
                                rows={2}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>Keywords (comma-separated)</Label>
                              <Input
                                value={q.keywords}
                                onChange={(e) => updateQuestion(index, 'keywords', e.target.value)}
                                placeholder="e.g. leadership, teamwork, problem-solving"
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/jobs/${params.id}`)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
